import infoIcon from "@assets/actions/info.svg";
import tooltipBgDesktop from "@assets/images/TooltipBgDesktop.svg";
import tooltipBgMobile from "@assets/images/TooltipBgMobile.svg";
import { NEWSLETTER_MESSAGES } from "@config/NewsletterFormConstants";
import fetchTMApi from "@helper/fetchTMApi";
import { getValidEmailRegex } from "@helper/utils";
import useWindowDimensions from "@hooks/useWindowDimensions";
import { NewsletterPostResponse } from "@typings/Newsletter";
import type { ReactElement } from "react";
import { FormEvent, useEffect, useState } from "react";
type Props = Readonly<{
  testId?: string;
  mobwebview?: boolean;
}>;
export default function NewsletterSignupForm({
  testId = "newsletter-form",
  mobwebview = false,
}: Props): ReactElement {
  const [email, setEmail] = useState<string>("");
  const [isMarketingChecked, setIsMarketingChecked] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const { width } = useWindowDimensions();

  async function handleOnSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    const isEmailValid = email && getValidEmailRegex().test(email);

    if (!isEmailValid) {
      setErrorMsg(NEWSLETTER_MESSAGES.INVALID_EMAIL);
      return;
    }
    try {
      const queryParams = new URLSearchParams({
        email,
        isMarketingChecked: String(isMarketingChecked),
      }).toString();
      const res = await fetchTMApi(`newsletterSignup?${queryParams}`, "GET");
      const data = (await res) as NewsletterPostResponse;
      if (data.msg) {
        setErrorMsg(""); // unset error message
        setSuccessMsg(data.msg);
        return;
      }

      if (data.errorMsg) {
        setErrorMsg(data.errorMsg);
        return;
      }
    } catch (e: unknown) {
      setErrorMsg(NEWSLETTER_MESSAGES.FAILED);
      console.log("failed with error: ", e);
      return;
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const handleMarketingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsMarketingChecked(e.target.checked);
  };

  useEffect(() => {
    if (errorMsg.length > 0 || successMsg.length > 0) {
      setTimeout(() => {
        setErrorMsg("");
        setSuccessMsg("");
      }, 3000);
    }
  }, [errorMsg, successMsg]);

  return (
    <>
      {errorMsg ? (
        <div className="w-full py-3 px-5 mb-4 border border-solid border-red-200 rounded text-white-300 bg-pink-100 text-xs font-normal leading-800">
          {errorMsg}
        </div>
      ) : successMsg ? (
        <div className="w-full py-3 px-5 mb-4 border border-solid border-green-200 rounded text-green-300 bg-green-100 text-xs font-normal leading-800">
          {successMsg}
        </div>
      ) : (
        <>
          <div className="text-xs leading-800">
            மின்னஞ்சல் பதிவு செய்வதன் மூலம் செய்திகள், நிகழ்ச்சிகள் மற்றும் பல
            தகவல்களை மின்னஞ்சல் வழி பெறுவீர்கள்!
          </div>
          <form
            onSubmit={(e: FormEvent<HTMLFormElement>) => void handleOnSubmit(e)}
            data-testid={testId}
            className="w-full"
          >
            <div>
              <input
                id="enter-email"
                name="enter-email"
                onChange={handleInputChange}
                type="email"
                className="block w-full h-9 py-1.5 px-3 mt-1 text-xs font-normal leading-800 text-gray-1000 bg-white bg-clip-padding border border-solid border-gray-900 rounded transition ease-in-out duration-150 delay-0 focus:outline-none focus:border-pink-400 focus:shadow-5xl"
                placeholder="மின்னஞ்சல் முகவரியை இங்கு பதிவிடுக!"
                required={true}
                maxLength={254}
                size={60}
              />
            </div>

            <div className="text-xs font-normal leading-900 text-gray-100">
              இதற்குப் பதிவு செய்வதன்வழி, அவ்வப்போது மாறுதலுக்கு உள்ளாகும்
              எஸ்பிஎச் மீடியா நிறுவனத்தின்{" "}
              <a
                href={`https://www.sph.com.sg/tnc/website${mobwebview ? "?platform=mobile" : ""}`}
                target="_blank"
                className="text-red-100 hover:text-red-200 hover:underline"
                rel="noreferrer"
              >
                விதிமுறைகள் மற்றும் நிபந்தனைகள்
              </a>{" "}
              மற்றும்{" "}
              <a
                href={`https://www.sph.com.sg/tnc/pdpa${mobwebview ? "?platform=mobile" : ""}`}
                target="_blank"
                className="text-red-100 hover:text-red-200 hover:underline"
                rel="noreferrer"
              >
                தனிமனிதத் தரவுப் பாதுகாப்பு அறிவிப்பு
              </a>{" "}
              யையும் ஏற்றுக்கொள்கிறேன்.
            </div>
            <div className="flex items-center my-4 space-x-2">
              <input
                name="consent-email"
                id="consent-email"
                type="checkbox"
                onChange={handleMarketingChange}
                className="appearance-auto h-4 w-4 text-blue-400 border-blue-400 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="consent-email"
                className="lg:flex gap-1 items-center text-xs text-gray-100"
              >
                ஆம், விளம்பரத் தகவல்களையும் சலுகைகளையும் பெற விரும்புகிறேன்.
                <div className="relative group inline-block">
                  {/* Info icon */}
                  <img
                    src={infoIcon}
                    alt="info"
                    width={12}
                    className="cursor-pointer"
                  />

                  {/* Tooltip Desktop*/}
                  <div
                    className={`absolute z-10 -top-16 ${width <= 375 ? "-left-[130px]" : "-left-[116px]"} lg:top-1/2 lg:left-full -translate-y-1/2 rounded-md lg:ml-1
                w-[245px] h-[130px] lg:w-[235px] lg:h-[110px] px-2 pt-2 pb-[19.5px] lg:py-[6px] lg:pl-[18px] lg:pr-[6px]
                invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300`}
                    style={{
                      backgroundImage: `url(${width <= 992 ? tooltipBgMobile : tooltipBgDesktop})`,
                      backgroundSize: "cover",
                    }}
                  >
                    <div className="w-[200px] lg:w-[210px] pl-2 text-gray-100 text-3xs leading-[150%]">
                      எஸ்பிஎச் மீடியா லிமிடெட், அதைச் சார்ந்த நிறுவனங்கள், இணை
                      நிறுவனங்கள் மற்றும் அவற்றின் முகவர்கள், அங்கீகாரம் பெற்ற
                      சேவை வழங்குநர்கள்.
                    </div>
                  </div>
                </div>
              </label>
            </div>
            <button className="w-full align-middle select-none py-1.5 px-3 text-xs leading-800 rounded transition ease-in-out duration-150 delay-0 border-0 mt-1 mb-2 text-white-100 bg-100 relative">
              அனுப்பு
            </button>
          </form>
        </>
      )}
    </>
  );
}
